import type { Campaign } from '~/types/graphql';

import { create } from 'zustand';

export const DEFAULT_TIME_HIDDEN_ICON_LOADING = 3000;

export enum IThemePagePublishStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  DRAFT = 'draft',
}

export type ThemePageIdValueType = {
  status: IThemePagePublishStatus;
  isLoading: boolean;
  loadingTime?: number;
};

const DEFAULT_THEME_PAGE_ID_VALUE: ThemePageIdValueType = {
  status: IThemePagePublishStatus.DRAFT,
  isLoading: false,
};

export interface IModalPublishCampaignStore {
  isOpen: boolean;
  themePageIds: Record<string, ThemePageIdValueType>;
  //publishedThemePageIds:Persists data across modal open/close cycles - retains history
  publishedThemePageIds: string[];
  campaigns: Campaign[];
  onOpenModal: (themePageIds: string[], onPublishAndStartCampaign: () => void) => void;
  closeModal: () => void;
  onPublishAndStartCampaign: () => void;
  updateThemePageId: (themePageId: string, themePageIdValueType: ThemePageIdValueType) => void;
  publishThemePageSuccess: (themePageId: string) => void;
  publishThemePageFailed: (themePageId: string) => void;
  setCampaigns: (campaigns: Campaign[]) => void;
  clearFailedThemePages: () => void;
  resetData: () => void;
}

export const useModalPublishCampaignStore = create<IModalPublishCampaignStore>((set) => ({
  isOpen: false,
  themePageIds: {},
  publishedThemePageIds: [],
  campaigns: [],
  setCampaigns: (campaigns) => set({ campaigns }),
  onOpenModal: (themePageIds, onPublishAndStartCampaign) =>
    set(() => ({
      isOpen: true,
      themePageIds: themePageIds.reduce(
        (acc, themePageId) => ({
          ...acc,
          [themePageId]: DEFAULT_THEME_PAGE_ID_VALUE,
        }),
        {},
      ),
      onPublishAndStartCampaign,
    })),

  closeModal: () => set({ isOpen: false }),
  onPublishAndStartCampaign: () => set({ isOpen: false }),
  updateThemePageId: (themePageId, themePageIdValueType) =>
    set((state) => ({
      themePageIds: {
        ...state.themePageIds,
        [themePageId]: themePageIdValueType,
      },
    })),
  publishThemePageSuccess: (themePageId) =>
    set((state) => ({
      themePageIds: {
        ...state.themePageIds,
        [themePageId]: {
          ...state.themePageIds[themePageId],
          status: IThemePagePublishStatus.SUCCESS,
          isLoading: false,
          loadingTime: DEFAULT_TIME_HIDDEN_ICON_LOADING,
        },
      },
      publishedThemePageIds: [...state.publishedThemePageIds, themePageId],
    })),
  publishThemePageFailed: (themePageId) =>
    set((state) => ({
      themePageIds: {
        ...state.themePageIds,
        [themePageId]: {
          ...state.themePageIds[themePageId],
          status: IThemePagePublishStatus.FAILED,
          isLoading: false,
        },
      },
    })),
  // Drop only the FAILED publish entries, leaving success/in-progress state and
  // the published history intact. Used to reconcile the stale in-memory fail
  // state once a campaign has successfully started (status RUNNING), so the
  // "Starting failed" banner cannot resurface (e.g. after a subsequent pause).
  clearFailedThemePages: () =>
    set((state) => ({
      themePageIds: Object.fromEntries(
        Object.entries(state.themePageIds).filter(
          ([, value]) => value.status !== IThemePagePublishStatus.FAILED,
        ),
      ),
    })),
  resetData: () => {
    set(() => ({
      themePageIds: {},
      publishedThemePageIds: [],
    }));
  },
}));
